/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import { SpinnerBlock } from "../common/Spinner";
import * as authSelectors from "../auth/authSelectors";
import ProductCategoryListRow from "./ProductCategoryListRow";
import { routes } from "../../appRoutingHelpers";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { ButtonInternalLink } from "../../components/NewButtons";
import { DragAndDropContainer } from "../../components/DragAndDropContainer";
import { css } from "@emotion/react";
import {
  useGetProductCategories,
  useUpdateProductCategorySorting,
} from "./service/product-categories-api";
import { ProductCategoryListItem } from "./models/product-category";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { toastr } from "react-redux-toastr";

interface Props {}

const dndContainerStyle = css`
  width: 100%;
`;

export const ProductCategoryList: React.FC<Props> = (props) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const {
    data: productCategories,
    isLoading: isLoadingProductCategories,
    error: listProductCategoriesError,
  } = useGetProductCategories(organization);

  const {
    mutateAsync: updateProductCategorySorting,
    isLoading: isUpdatingProductCategorySorting,
  } = useUpdateProductCategorySorting();

  useHandleFetchError(listProductCategoriesError);

  if (
    isLoadingProductCategories ||
    !productCategories ||
    isUpdatingProductCategorySorting
  ) {
    return <SpinnerBlock />;
  }

  return (
    <div className="container mt-7">
      <div className="row row-cards row-deck">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom-0 mt-4 mb-4 justify-content-between">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi card-title"
              >
                Product Category List
              </StyledText>
              <ButtonInternalLink
                to={routes.shopProductCategoryNew(organization)}
              >
                Add new
              </ButtonInternalLink>
            </div>
            <div className="pl-5 pr-5">
              <Table>
                <thead>
                  <TableRowHeader>
                    <TableCellHeaderText className="pl-3">
                      Reorder
                    </TableCellHeaderText>
                    <TableCellHeaderText>Image</TableCellHeaderText>
                    <TableCellHeaderText>Name</TableCellHeaderText>
                    <TableCellHeaderText>Title</TableCellHeaderText>
                  </TableRowHeader>
                </thead>
                <DragAndDropContainer
                  containerStyle={dndContainerStyle}
                  isTable={true}
                  onEnd={(_, containerRef) => {
                    if (!containerRef) {
                      return;
                    }
                    // iterating over dom children. Each of them has the original index as data attribute
                    // @ts-expect-error
                    const sorting = [...containerRef.current?.children].map(
                      (b) => b.dataset.id
                    );

                    try {
                      updateProductCategorySorting({
                        organizationKeyName: organization,
                        data: {
                          sort: sorting,
                        },
                      });
                      toastr.success(
                        "Success",
                        "Product categories reordered successfully"
                      );
                    } catch (error: unknown) {
                      const message = getErrorMessage(error);
                      toastr.error("Error", message);
                    }
                  }}
                >
                  <>
                    {(productCategories || [])
                      .sort((a, b) => a.sortKey - b.sortKey)
                      ?.map((pc: ProductCategoryListItem, i: number) => {
                        return (
                          <ProductCategoryListRow
                            key={i}
                            href={routes.shopProductCategoryEdit(
                              organization,
                              pc.id
                            )}
                            productCategory={pc}
                          />
                        );
                      })}
                  </>
                </DragAndDropContainer>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCategoryList;
